import type {
  QueueGroup,
  QueueKanbanColumn,
  QueueTone
} from '@/components/designSystem/QueueViews/types'
import type {
  PriorityCheckStatus,
  PriorityConfig,
  PriorityContactChannel,
  PriorityPaymentRow,
  PriorityPaymentType,
  PrioritySituation,
  PrioritySlaState
} from '@/Network/Types/Responses/PriorityPaymentsResponses'

import type { PriorityGroupBy } from './composables/usePriorityGrouping'
import { PRIORITY_SLA_STATE_LABELS, formatThresholdHint, priorityRowLiveState } from './slaFormat'

/** Chave da preferência de visão no localStorage (Lista · Tabela · Kanban). */
export const PRIORITY_SCREEN_KEY = 'priority-payments'

export const PRIORITY_SITUATION_LABELS: Record<PrioritySituation, string> = {
  FUTURE: 'Futuro',
  IN_PROGRESS: 'Em andamento',
  RESOLVED_APP: 'Check completo pelo app',
  RESOLVED_SHEET: 'Resolvido pela folha',
  // Horário confirmado OK por origem antiga (lançamento manual legado). Texto
  // NOVO — o mockup foi desenhado antes de este estado existir no contrato.
  RESOLVED_OTHER: 'Resolvido por outra origem',
  APPROVED: 'Aprovado',
  AWAITING_AUDIT: 'Aguardando auditoria',
  REJECTED: 'Rejeitado',
  NOT_SENT: 'Não enviado',
  NOT_REQUIRED: 'Registro não exigido'
}

export const PRIORITY_SITUATION_TONES: Record<PrioritySituation, QueueTone> = {
  FUTURE: 'neutral',
  IN_PROGRESS: 'neutral',
  RESOLVED_APP: 'neutral',
  RESOLVED_SHEET: 'ok',
  RESOLVED_OTHER: 'ok',
  APPROVED: 'ok',
  AWAITING_AUDIT: 'info',
  REJECTED: 'crit',
  NOT_SENT: 'warn',
  NOT_REQUIRED: 'neutral'
}

/** Canais do "Registrar contato" (Q20). */
export const PRIORITY_CONTACT_CHANNEL_LABELS: Record<PriorityContactChannel, string> = {
  WHATSAPP: 'WhatsApp',
  PHONE: 'Ligação',
  OTHER: 'Outro'
}

/** Teto de `note` no contrato: `note ≤ 500`. */
export const PRIORITY_CONTACT_NOTE_MAX_LENGTH = 500

/**
 * `IN_CASH_EXCEPTION` e `IN_CASH_POLICY` dizem a MESMA coisa para quem cobra:
 * "À vista". A origem (exceção × política) importa ao backend, não à Operação.
 */
export const PRIORITY_TYPE_LABELS: Record<PriorityPaymentType, string> = {
  IN_CASH_EXCEPTION: 'À vista',
  IN_CASH_POLICY: 'À vista',
  ANTECIPE_REQUESTED: 'Antecipe solicitado',
  ANTECIPE_PAID: 'Antecipe pago'
}

export const PRIORITY_CHECK_LABELS: Record<PriorityCheckStatus, string> = {
  NONE: 'Sem check',
  PARTIAL: 'Só check-in',
  PENDING: 'Pendente',
  OK: 'Completo'
}

/** `'Rejeitado · 2ª vez'` — a contagem só entra a partir da segunda rejeição (Q6). */
export const prioritySituationLabel = (row: PriorityPaymentRow): string => {
  const base = PRIORITY_SITUATION_LABELS[row.situation]
  const count = row.activityRecord?.rejectionCount ?? 0
  return row.situation === 'REJECTED' && count > 1 ? `${base} · ${count}ª vez` : base
}

/**
 * Grupos de situação do M3. São os MESMOS no filtro "Situação" da barra
 * (`label`, com contagem) e no "Agrupar por Situação" (`title`): um lugar só
 * para uma situação nova do contrato entrar nos dois.
 */
export interface PrioritySituationGroup {
  key: string
  label: string
  title: string
  hint: string
  tone: QueueTone
  defaultOpen: boolean
  situations: PrioritySituation[]
}

export const PRIORITY_SITUATION_GROUPS: PrioritySituationGroup[] = [
  {
    key: 'notSent',
    label: PRIORITY_SITUATION_LABELS.NOT_SENT,
    title: PRIORITY_SITUATION_LABELS.NOT_SENT,
    hint: 'prazo de envio',
    tone: 'warn',
    defaultOpen: true,
    situations: ['NOT_SENT']
  },
  {
    key: 'rejected',
    label: PRIORITY_SITUATION_LABELS.REJECTED,
    title: PRIORITY_SITUATION_LABELS.REJECTED,
    hint: 'volta ao prazo de envio',
    tone: 'crit',
    defaultOpen: true,
    situations: ['REJECTED']
  },
  {
    key: 'awaitingAudit',
    label: 'Aguardando auditoria',
    title: 'Na auditoria',
    hint: 'prazo de auditoria',
    tone: 'info',
    defaultOpen: true,
    situations: ['AWAITING_AUDIT']
  },
  {
    key: 'resolved',
    label: 'Aprovado / resolvido',
    title: 'Aprovado / resolvido',
    hint: 'horário confirmado',
    tone: 'ok',
    defaultOpen: false,
    situations: ['APPROVED', 'RESOLVED_SHEET', 'RESOLVED_OTHER']
  },
  {
    key: 'app',
    label: 'Check completo pelo app',
    title: 'Check pelo app',
    hint: 'concluído no check-out',
    tone: 'neutral',
    defaultOpen: false,
    situations: ['RESOLVED_APP']
  },
  {
    key: 'upcoming',
    label: 'Em andamento / futuro',
    title: 'Em andamento / futuro',
    hint: 'plantão em andamento ou futuro',
    tone: 'neutral',
    defaultOpen: false,
    situations: ['IN_PROGRESS', 'FUTURE']
  },
  {
    // Q1 (spec 2026-09-25 §2.2) — médico fora do time: nada a cobrar, fechado por padrão.
    key: 'notRequired',
    label: PRIORITY_SITUATION_LABELS.NOT_REQUIRED,
    title: PRIORITY_SITUATION_LABELS.NOT_REQUIRED,
    hint: 'médico fora do time',
    tone: 'neutral',
    defaultOpen: false,
    situations: ['NOT_REQUIRED']
  }
]

export interface PriorityGroupColumn extends QueueKanbanColumn<PriorityPaymentRow> {
  hint: string
  defaultOpen: boolean
  /** Só no agrupamento por estado: a coluna/seção mostra o selo do estado. */
  state?: PrioritySlaState
  /** Coluna que só aparece quando alguma linha cai nela. */
  optional?: boolean
  /**
   * Filtro do grupo no servidor (carga por coluna do Kanban). Ausente = o
   * contrato não tem filtro para o grupo (ex.: "Sem SLA").
   */
  serverFilter?: PriorityColumnServerFilter
}

/** Recorte de UM grupo na listagem: `slaState[]` ou `situations[]` do contrato. */
export interface PriorityColumnServerFilter {
  slaState?: PrioritySlaState[]
  situations?: PrioritySituation[]
}

export interface PriorityGroupingContext {
  nowMs: number | null
  config: PriorityConfig | null | undefined
}

const NOT_STARTED_HINT = 'plantão em andamento ou futuro'
const CONFIRMED_HINT = 'horário confirmado'

const stateColumns = (context: PriorityGroupingContext): PriorityGroupColumn[] => {
  const stateOf = (row: PriorityPaymentRow) =>
    priorityRowLiveState(row, context.nowMs, context.config)
  const column = (
    state: PrioritySlaState,
    tone: QueueTone,
    hint: string,
    defaultOpen: boolean,
    optional = false
  ): PriorityGroupColumn => ({
    key: state,
    title: PRIORITY_SLA_STATE_LABELS[state],
    tone,
    hint,
    defaultOpen,
    state,
    optional,
    serverFilter: { slaState: [state] },
    matches: (row) => stateOf(row) === state
  })
  return [
    column('OVERDUE', 'crit', 'prazo já passou', true),
    column('URGENT', 'warn', formatThresholdHint(context.config, 'urgent'), true),
    column('ATTENTION', 'warn', formatThresholdHint(context.config, 'attention'), true),
    column('ON_TIME', 'info', 'com folga', true),
    column('NOT_STARTED', 'neutral', NOT_STARTED_HINT, false, true),
    column('DONE', 'ok', CONFIRMED_HINT, false),
    {
      key: 'NO_SLA',
      title: 'Sem SLA',
      tone: 'neutral',
      hint: 'prazo ainda não calculado pelo servidor',
      defaultOpen: true,
      optional: true,
      matches: (row) => stateOf(row) === null
    }
  ]
}

const isSituation =
  (...situations: PrioritySituation[]) =>
  (row: PriorityPaymentRow): boolean =>
    situations.includes(row.situation)

/** Predicado e filtro do servidor da MESMA lista de situações: não divergem. */
const bySituations = (
  ...situations: PrioritySituation[]
): Pick<PriorityGroupColumn, 'matches' | 'serverFilter'> => ({
  matches: isSituation(...situations),
  serverFilter: { situations }
})

/** Etapa pela situação (M3): exaustivo sobre as 10 situações do contrato. */
const STAGE_COLUMNS: PriorityGroupColumn[] = [
  {
    key: 'send',
    title: 'Enviar',
    tone: 'warn',
    hint: 'registro ainda não enviado',
    defaultOpen: true,
    ...bySituations('NOT_SENT')
  },
  {
    key: 'resend',
    title: 'Reenviar',
    tone: 'crit',
    hint: 'rejeitado pela auditoria',
    defaultOpen: true,
    ...bySituations('REJECTED')
  },
  {
    key: 'audit',
    title: 'Auditar',
    tone: 'info',
    hint: 'registro na fila da auditoria',
    defaultOpen: true,
    ...bySituations('AWAITING_AUDIT')
  },
  {
    key: 'notStarted',
    title: 'Não iniciado',
    tone: 'neutral',
    hint: NOT_STARTED_HINT,
    defaultOpen: false,
    optional: true,
    ...bySituations('FUTURE', 'IN_PROGRESS')
  },
  {
    key: 'done',
    title: 'Concluídos',
    tone: 'ok',
    hint: CONFIRMED_HINT,
    defaultOpen: false,
    ...bySituations('APPROVED', 'RESOLVED_SHEET', 'RESOLVED_OTHER', 'RESOLVED_APP')
  },
  {
    // Q1 (spec 2026-09-25 §2.2) — fora do time não tem etapa a cumprir; só aparece com linha.
    key: 'notRequired',
    title: PRIORITY_SITUATION_LABELS.NOT_REQUIRED,
    tone: 'neutral',
    hint: 'médico fora do time',
    defaultOpen: false,
    optional: true,
    ...bySituations('NOT_REQUIRED')
  }
]

const SITUATION_COLUMNS: PriorityGroupColumn[] = PRIORITY_SITUATION_GROUPS.map((group) => ({
  key: group.key,
  title: group.title,
  tone: group.tone,
  hint: group.hint,
  defaultOpen: group.defaultOpen,
  // `notRequired` (Q1) também é opcional: coluna vazia antes do backend gravar NOT_REQUIRED.
  optional: group.key === 'upcoming' || group.key === 'notRequired',
  ...bySituations(...group.situations)
}))

/**
 * Colunas do Kanban e seções da Lista para o "Agrupar por" escolhido. Sem
 * coluna "Atrasados" (Q6/Q19): o atrasado de ciclo anterior é Estourado. As
 * colunas opcionais (Não iniciado, Sem SLA, Em andamento / futuro) só entram
 * quando alguma linha cai nelas. A ordem DENTRO do grupo é a do servidor.
 */
export const priorityGroupColumns = (
  groupBy: PriorityGroupBy,
  rows: PriorityPaymentRow[],
  context: PriorityGroupingContext,
  /** Totais do servidor por coluna: a opcional com total > 0 também aparece. */
  totals?: Partial<Record<string, number>>
): PriorityGroupColumn[] =>
  priorityColumnDefinitions(groupBy, context).filter(
    (column) => !column.optional || rows.some(column.matches) || (totals?.[column.key] ?? 0) > 0
  )

/** Todas as colunas do "Agrupar por", inclusive as opcionais vazias. */
export const priorityColumnDefinitions = (
  groupBy: PriorityGroupBy,
  context: PriorityGroupingContext
): PriorityGroupColumn[] => {
  if (groupBy === 'SLA_STATE') return stateColumns(context)
  if (groupBy === 'STAGE') return STAGE_COLUMNS
  return SITUATION_COLUMNS
}

/** Seções da Lista = colunas do Kanban (mesma função), sem as vazias. */
export const priorityGroupSections = (
  groupBy: PriorityGroupBy,
  rows: PriorityPaymentRow[],
  context: PriorityGroupingContext
): QueueGroup<PriorityPaymentRow>[] =>
  priorityGroupColumns(groupBy, rows, context)
    .map((column) => ({
      key: column.key,
      title: column.title,
      tone: column.tone,
      hint: column.hint,
      defaultOpen: column.defaultOpen,
      rows: rows.filter(column.matches)
    }))
    .filter((section) => section.rows.length > 0)
